import { useId } from 'react';
import type { VoterDataPurposeDto } from '@signalvote/shared';
import { ListPurpose } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { EstimateMark, VoterEstimate } from './VoterCount';
import { voterDataCountPromise } from './voterDataOrder';
import type { ListEstimate } from './voterEstimate';
import { formatDollars, nounFor, purposeTerms } from './voterPurpose';

/**
 * "What's this list for?" — Doors, Phones, Mail or Turnout. Before an answer it is the whole
 * page; afterwards it shrinks to a row above the sentence, so the purpose can be changed at any
 * time without losing the customer's rules.
 */
export function VoterDataPurposePicker({
  purposes,
  value,
  onChange,
}: {
  purposes: VoterDataPurposeDto[];
  value: ListPurpose | null;
  onChange: (purpose: VoterDataPurposeDto) => void;
}) {
  const questionId = useId();
  const compact = value !== null;
  return (
    <div className={compact ? 'flex flex-wrap items-center gap-x-3 gap-y-2' : 'py-10 sm:py-14'}>
      {compact ? (
        <p id={questionId} className="text-sm font-medium text-muted-foreground">
          What’s this list for?
        </p>
      ) : (
        <h2 id={questionId} className="text-[clamp(1.75rem,1.15rem+1.7vw,2.5rem)] font-medium tracking-[-0.024em] text-foreground">
          What’s this list for?
        </h2>
      )}
      <div
        role="radiogroup"
        aria-labelledby={questionId}
        className={compact ? 'inline-flex flex-wrap gap-1 rounded-lg bg-muted p-1' : 'mt-6 grid gap-3 sm:grid-cols-2'}
      >
        {purposes.map((p) => {
          const checked = p.purpose === value;
          return (
            <button
              key={p.purpose}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => onChange(p)}
              className={cn(
                'text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                compact
                  ? cn('rounded-md px-3 py-1.5 text-sm font-semibold', checked ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')
                  : 'rounded-xl border border-border p-4 hover:border-primary active:scale-[0.99]',
              )}
            >
              <span className={compact ? '' : 'block text-lg font-semibold text-foreground'}>{p.label}</span>
              {!compact && <span className="mt-1 block text-sm text-muted-foreground">{p.description}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function VoterDataListSummary({
  purpose,
  list,
  pricePerRecordCents,
}: {
  purpose: VoterDataPurposeDto;
  list: ListEstimate | null;
  pricePerRecordCents: number | undefined;
}) {
  const columnsId = useId();
  const terms = list ? purposeTerms(purpose.purpose, list) : null;
  return (
    <section aria-label="List summary" className="mb-10 grid gap-6 rounded-xl border border-border p-5 sm:grid-cols-[1fr_auto]">
      <div className="space-y-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground" id={columnsId}>
            Columns you’ll receive
          </h3>
          <ul aria-labelledby={columnsId} className="mt-2 flex flex-wrap gap-1.5">
            {purpose.columns.map((column) => (
              <li key={column} className="rounded-md bg-muted px-2 py-0.5 text-xs text-foreground/80">
                {column}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {list && (
        <div className="text-left sm:text-right">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Price</p>
          <p className="text-3xl font-semibold tabular-nums text-foreground">
            <VoterEstimate estimate={list.priceCents} dollars />
          </p>
          <p className="text-sm tabular-nums text-muted-foreground">
            <VoterEstimate estimate={list.records} /> {nounFor(list.records.value, 'voter')}
            {pricePerRecordCents !== undefined && ` × ${formatDollars(pricePerRecordCents)}`}
          </p>
          {terms && (
            <p className="mt-2 text-sm font-medium tabular-nums text-foreground/80">
              {terms.estimated ? <EstimateMark>{terms.text}</EstimateMark> : terms.text}
            </p>
          )}
          <p className="mt-2 max-w-xs text-xs text-muted-foreground sm:ml-auto">
            {voterDataCountPromise(list.records.value, false)}
          </p>
        </div>
      )}
    </section>
  );
}
